import { DealPipelineInvestmentsQuery } from '@/__generated__/graphql';
import { RejectedBannerIcon } from '@/assets/icons/info-icons/rejectedBanner';
import { Banner } from '@/components/basicComponents/banner';
import ModalLoader from '@/components/basicComponents/modal/modalLoader';
import ModalWrappedContent from '@/components/basicComponents/modal/modalWrappedContent';
import { useErrorMessages } from '@/hooks/i18n';
import { themeVar } from '@/styles/theme';
import { useMutation } from '@apollo/client';
import { useState } from 'react';
import styled from 'styled-components';
import { DEAL_PIPELINE_INVESTMENTS_QUERY, DEAL_PIPELINE_PAGE_EXECUTE_WORKFLOW_TRANSITION_MUTATION } from '../queries';

interface TerminateProps {
  isOpen: boolean;
  currentRow: DealPipelineInvestmentsQuery['Investments']['data'][number] | null;
  onClose: () => void;
}

const Terminate = ({ isOpen, currentRow, onClose }: TerminateProps) => {
  const { default: defaultErrorMessage } = useErrorMessages();

  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [executeWorkflowTransition, { loading: executeWorkflowTransitionLoading }] = useMutation(
    DEAL_PIPELINE_PAGE_EXECUTE_WORKFLOW_TRANSITION_MUTATION,
    {
      awaitRefetchQueries: true,
      refetchQueries: [DEAL_PIPELINE_INVESTMENTS_QUERY]
    }
  );

  const saveValue = async () => {
    try {
      setErrorMessage(null);

      if (!currentRow) {
        setErrorMessage(defaultErrorMessage);
        return;
      }

      const workflowTransitionIndex = currentRow.availableWorkflowTransitions.findIndex((transition) => transition.name === 'terminate');

      if (workflowTransitionIndex === -1) {
        setErrorMessage(defaultErrorMessage);
        return;
      }

      await executeWorkflowTransition({
        variables: {
          data: {
            instanceId: currentRow.workflowInstance.id,
            transitionId: currentRow.availableWorkflowTransitions[workflowTransitionIndex].id
          }
        }
      });

      onClose();
    } catch (error) {
      console.error(error);
      setErrorMessage(defaultErrorMessage);
    }
  };

  if (!currentRow) return <></>;

  return (
    <ModalWrappedContent
      isOpen={isOpen}
      onClose={onClose}
      confirmButton={{
        name: 'Confirm',
        onClick: saveValue,
        disabled: executeWorkflowTransitionLoading
      }}
      showCancelButton={!executeWorkflowTransitionLoading}
      showCloseIcon={!executeWorkflowTransitionLoading}
      isUseOutsideClick={!executeWorkflowTransitionLoading}
    >
      {executeWorkflowTransitionLoading ? (
        <ModalLoader />
      ) : (
        <>
          <ModalBody>
            <Text fontWeight="400">
              Please confirm you want to terminate <Text fontWeight="700">{currentRow.name}</Text>
            </Text>
          </ModalBody>
          {errorMessage && (
            <ErrorBannerWrapper>
              <Banner
                icon={<RejectedBannerIcon fill={themeVar('context.light')} />}
                title="Error"
                description={errorMessage}
                bgColor={themeVar('context.error')}
              />
            </ErrorBannerWrapper>
          )}
        </>
      )}
    </ModalWrappedContent>
  );
};

export default Terminate;

const ModalBody = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 35px;
`;

const Text = styled.span<{ fontWeight: string }>`
  color: ${themeVar('font.base')};
  font-size: 33px;
  text-align: center;
  font-weight: ${({ fontWeight }) => fontWeight};
  line-height: 46.2px;
`;

const ErrorBannerWrapper = styled.div`
  margin-top: 10px;
`;
